/**
 * Supabase OAuth redirect helpers.
 *
 * PKCE exchange must hit `/api/auth/callback` (Route Handler) so the
 * code-verifier cookie set by `@supabase/ssr` `createBrowserClient` is
 * readable on the server.
 *
 * Important: `redirectTo` for Google OAuth must be an **exact** allow-list
 * match (no dynamic `?next=`). Locale / post-login path are stored in cookies
 * instead. Email magic-links that already use `?next=` need a wildcard like
 * `https://kleaners.bg/api/auth/callback**` in Supabase Auth → Redirect URLs.
 *
 * After the code exchange, the route handler must redirect to the public site.
 * Behind Traefik/Coolify, `request.url` is the container origin
 * (`https://localhost:3000`), which would send the browser to localhost.
 */

import {
  CANONICAL_HOST,
  CANONICAL_ORIGIN,
  LEGACY_REDIRECT_HOSTS,
  hostnameWithoutPort,
} from '@/lib/site-identity';

export const OAUTH_LOCALE_COOKIE = 'kleaners_oauth_locale';
export const OAUTH_NEXT_COOKIE = 'kleaners_oauth_next';

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

export interface AuthRedirectOriginInput {
  /** Origin from `request.url` / `nextUrl` (often the container, not the browser). */
  requestOrigin: string;
  host?: string | null;
  forwardedHost?: string | null;
  forwardedProto?: string | null;
  /** `NEXT_PUBLIC_SITE_URL`, when set to a public origin. */
  siteUrl?: string | null;
}

function firstHeaderValue(value: string | null | undefined): string {
  return value?.split(',')[0]?.trim() ?? '';
}

function isLoopbackHost(hostname: string): boolean {
  return LOOPBACK_HOSTS.has(hostname.toLowerCase());
}

function normalizePublicSiteUrl(siteUrl: string | null | undefined): string | null {
  const trimmed = siteUrl?.trim();
  if (!trimmed) {
    return null;
  }
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withProtocol);
    if (isLoopbackHost(url.hostname)) {
      return null;
    }
    return url.origin;
  } catch {
    return null;
  }
}

function allowedPublicHosts(siteUrl: string | null): Set<string> {
  const hosts = new Set<string>([CANONICAL_HOST, ...LEGACY_REDIRECT_HOSTS]);
  if (siteUrl) {
    try {
      hosts.add(new URL(siteUrl).hostname.toLowerCase());
    } catch {
      // Ignore a malformed site URL; canonical host still applies.
    }
  }
  return hosts;
}

function isAllowedPublicHost(hostname: string, siteUrl: string | null): boolean {
  return allowedPublicHosts(siteUrl).has(hostname.toLowerCase());
}

/**
 * Origin for post-auth `Location` headers.
 *
 * Trust only the public site and its legacy hosts. A loopback request origin
 * combined with HTTPS (the reverse proxy's forwarded proto) is the container
 * address, so the redirect uses the configured public site instead.
 */
export function resolveAuthRedirectOrigin(input: AuthRedirectOriginInput): string {
  const siteUrl = normalizePublicSiteUrl(input.siteUrl);
  const forwardedHost = firstHeaderValue(input.forwardedHost);
  const host = firstHeaderValue(input.host);
  const forwardedProto = firstHeaderValue(input.forwardedProto).replace(/:$/, '').toLowerCase();

  const publicHostname = [forwardedHost, host]
    .map(candidate => hostnameWithoutPort(candidate))
    .find(hostname => Boolean(hostname) && isAllowedPublicHost(hostname, siteUrl));

  if (publicHostname) {
    const proto =
      forwardedProto === 'http' || forwardedProto === 'https' ? forwardedProto : 'https';
    return `${proto}://${publicHostname}`;
  }

  let requestOrigin = input.requestOrigin.replace(/\/$/, '');
  try {
    const requestUrl = new URL(input.requestOrigin);
    requestOrigin = requestUrl.origin;
    const proxiedLoopback =
      isLoopbackHost(requestUrl.hostname) &&
      (requestUrl.protocol === 'https:' || forwardedProto === 'https');
    if (proxiedLoopback) {
      return siteUrl ?? CANONICAL_ORIGIN;
    }
  } catch {
    // Keep the raw request origin when it is not a URL.
  }

  return requestOrigin;
}

export function buildOAuthApiCallbackUrl(origin: string): string {
  return `${origin.replace(/\/$/, '')}/api/auth/callback`;
}

/**
 * Email / booking flows that already encode `next` in the URL.
 * Prefer wildcards in the Supabase allow-list for these.
 */
export function buildOAuthApiCallbackUrlWithNext(origin: string, nextPath: string): string {
  const next = nextPath.startsWith('/') ? nextPath : `/${nextPath}`;
  const params = new URLSearchParams({ next });
  return `${buildOAuthApiCallbackUrl(origin)}?${params.toString()}`;
}

export function resolveOAuthLocaleFromPathname(pathname: string): 'en' | 'de' | 'bg' {
  const match = pathname.match(/^\/(en|de|bg)(?:\/|$)/i);
  const locale = match?.[1]?.toLowerCase();
  if (locale === 'en' || locale === 'de' || locale === 'bg') {
    return locale;
  }
  return 'bg';
}

/** Persist locale + post-auth path so `/api/auth/callback` can redirect without query params. */
export function writeOAuthHandoffCookies(locale: 'en' | 'de' | 'bg', nextPath?: string): void {
  if (typeof document === 'undefined') {
    return;
  }

  const next = nextPath?.startsWith('/') ? nextPath : `/${locale}/auth/callback`;
  document.cookie = `${OAUTH_LOCALE_COOKIE}=${locale}; path=/; max-age=600; samesite=lax`;
  document.cookie = `${OAUTH_NEXT_COOKIE}=${encodeURIComponent(next)}; path=/; max-age=600; samesite=lax`;
}

/** True when the browser has a Supabase PKCE code-verifier cookie. */
export function hasPkceCodeVerifierCookie(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  return document.cookie.split(';').some(part => part.trim().includes('-code-verifier='));
}
